import { useEffect, useState, useRef } from "react";
import { supabase } from "../supabase-client";
import { ChevronLeft, ChevronRight, Play, Pause } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import portada from "../imagen/portada2.png";

export default function Inicio() {
   const [obras, setObras] = useState([]);
   // eslint-disable-next-line
   const [change, setChange] = useState(false);
   const [loading, setLoading] = useState(true);
   const [currentImageIndex, setCurrentImageIndex] = useState(0);
   const [isAutoRotating, setIsAutoRotating] = useState(true);
   const [isTransitioning, setIsTransitioning] = useState(false);

   // Use refs to avoid stale closure issues
   const currentImageIndexRef = useRef(0);
   const isTransitioningRef = useRef(false);

   // Update refs when state changes
   useEffect(() => {
      currentImageIndexRef.current = currentImageIndex;
   }, [currentImageIndex]);

   useEffect(() => {
      isTransitioningRef.current = isTransitioning;
   }, [isTransitioning]);

   const fetchObras = async () => {
      setLoading(true);
      const { error, data } = await supabase
         .from("obras")
         .select(
            `
        id,titulo,descripcion,medidas,precio,
        imagenes(texto,orden,imagen_url)
        `
         )
         .eq("id", 1)
         // Remove the imagen orden filter to get all images
         .order("created_at", { ascending: true });

      if (error) {
         setLoading(false);
         return;
      }

      // Sort images by orden
      if (data && data.length > 0 && data[0].imagenes) {
         data[0].imagenes.sort((a, b) => a.orden - b.orden);
      }

      setObras(data);
      setLoading(false);
   };

   // Gentle transition helper
   const changeImageWithTransition = (newIndex) => {
      if (isTransitioningRef.current) return; // Prevent multiple transitions

      setIsTransitioning(true);

      // Add a gentle fade effect
      setTimeout(() => {
         setCurrentImageIndex(newIndex);
         setTimeout(() => {
            setIsTransitioning(false);
         }, 100);
      }, 200);
   };

   // Auto-rotate images - Fixed version
   useEffect(() => {
      let interval = null;

      if (isAutoRotating && obras[0]?.imagenes?.length > 1) {
         interval = setInterval(() => {
            // Use callback to get current state
            setCurrentImageIndex((prevIndex) => {
               if (isTransitioningRef.current) return prevIndex;

               const nextIndex =
                  prevIndex === obras[0].imagenes.length - 1
                     ? 0
                     : prevIndex + 1;

               // Trigger transition
               setIsTransitioning(true);
               setTimeout(() => {
                  setIsTransitioning(false);
               }, 600);

               return nextIndex;
            });
         }, 6000); // 6 seconds for more relaxed viewing
      }

      return () => {
         if (interval) {
            clearInterval(interval);
         }
      };
   }, [isAutoRotating, obras]); // Remove currentImageIndex from dependencies

   useEffect(() => {
      fetchObras();
   }, [change]);

   const nextImage = () => {
      if (obras[0]?.imagenes?.length > 1) {
         const nextIndex =
            currentImageIndex === obras[0].imagenes.length - 1
               ? 0
               : currentImageIndex + 1;
         changeImageWithTransition(nextIndex);
      }
   };

   const prevImage = () => {
      if (obras[0]?.imagenes?.length > 1) {
         const prevIndex =
            currentImageIndex === 0
               ? obras[0].imagenes.length - 1
               : currentImageIndex - 1;
         changeImageWithTransition(prevIndex);
      }
   };

   const goToImage = (index) => {
      changeImageWithTransition(index);
   };

   const toggleAutoRotate = () => {
      setIsAutoRotating(!isAutoRotating);
   };

   return (
      <>
         {loading ? (
            <div className="flex justify-center items-center w-full min-h-[60vh]">
               <div className="flex flex-col items-center gap-4">
                  <div className="w-10 h-10 border-2 border-[#9B6D3F] border-t-transparent rounded-full animate-spin" />
                  <p className="text-[0.7rem] uppercase tracking-[0.2em] text-[#7A7570]">Cargando...</p>
               </div>
            </div>
         ) : obras && obras.length > 0 ? (
            <div className="min-h-[calc(100vh-68px)] flex flex-col md:flex-row">
               {/* Left: Full-bleed Carousel */}
               <div className="relative md:w-[58%] min-h-[100vw] md:min-h-[calc(100vh-68px)] overflow-hidden group bg-[#1A1A1A]">
                  <img
                     src={obras[0].imagenes[currentImageIndex]?.imagen_url}
                     alt={obras[0].imagenes[currentImageIndex]?.texto || `Imagen ${currentImageIndex + 1}`}
                     className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-in-out ${
                        isTransitioning ? "opacity-60 scale-[1.03]" : "opacity-100 scale-100"
                     } object-top md:object-center`}
                  />

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />

                  {/* Navigation arrows */}
                  {obras[0].imagenes?.length > 1 && (
                     <>
                        <button
                           onClick={prevImage}
                           disabled={isTransitioning}
                           className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white text-[#1A1A1A] opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:opacity-30"
                           aria-label="Imagen anterior"
                        >
                           <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                           onClick={nextImage}
                           disabled={isTransitioning}
                           className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white text-[#1A1A1A] opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:opacity-30"
                           aria-label="Imagen siguiente"
                        >
                           <ChevronRight className="w-5 h-5" />
                        </button>
                     </>
                  )}

                  {/* Dot indicators */}
                  {obras[0].imagenes?.length > 1 && (
                     <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                        {obras[0].imagenes.map((_, index) => (
                           <button
                              key={index}
                              onClick={() => goToImage(index)}
                              disabled={isTransitioning}
                              className={`transition-all duration-500 rounded-full ${
                                 index === currentImageIndex
                                    ? "w-6 h-1.5 bg-white rounded"
                                    : "w-1.5 h-1.5 bg-white/50 hover:bg-white/80"
                              }`}
                              aria-label={`Imagen ${index + 1}`}
                           />
                        ))}
                     </div>
                  )}

                  {/* Auto-rotate toggle */}
                  <button
                     onClick={toggleAutoRotate}
                     className="absolute bottom-6 right-5 text-white/50 hover:text-white/90 transition-colors opacity-0 group-hover:opacity-100"
                     aria-label={isAutoRotating ? "Pausar rotación" : "Iniciar rotación"}
                  >
                     {isAutoRotating ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
               </div>

               {/* Right: Artist Info */}
               <div className="md:w-[42%] flex flex-col justify-center px-8 md:px-12 lg:px-16 py-14 md:py-0 bg-[#FAFAF8]">
                  <motion.div
                     initial={{ opacity: 0, y: 24 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.9, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                     <p className="text-[0.68rem] uppercase tracking-[0.25em] text-[#9B6D3F] mb-8">
                        Buenos Aires, Argentina
                     </p>

                     <h1
                        className="text-[4rem] md:text-[4.5rem] lg:text-[5.5rem] font-medium text-[#1A1A1A] leading-[1.05] mb-10"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                     >
                        Diego<br />Curutchet
                     </h1>

                     <div className="w-10 h-[1px] bg-[#9B6D3F] mb-8" />

                     <p className="text-[0.92rem] text-[#7A7570] leading-relaxed mb-10 max-w-xs">
                        Escultor especializado en arte sacro y obras contemporáneas talladas en madera. Su trabajo profundo habla de la conexión entre el espíritu y la materia.
                     </p>

                     {/* CTAs */}
                     <div className="flex flex-col sm:flex-row gap-3 mb-12">
                        <Link
                           to="/obras"
                           className="inline-flex items-center justify-center gap-2 text-[0.72rem] uppercase tracking-[0.15em] text-white bg-[#9B6D3F] px-7 py-3.5 hover:bg-[#6B4A2A] transition-colors duration-300"
                        >
                           Ver Obras
                        </Link>
                        <Link
                           to="/muestras"
                           className="inline-flex items-center justify-center gap-2 text-[0.72rem] uppercase tracking-[0.15em] text-[#9B6D3F] border border-[#9B6D3F] px-7 py-3.5 hover:bg-[#9B6D3F] hover:text-white transition-colors duration-300"
                        >
                           Arte Sacro
                        </Link>
                     </div>

                     {/* Video preview */}
                     <div className="border-t border-[#EDE9E4] pt-8">
                        <div className="relative overflow-hidden mb-3">
                           <img
                              src={portada}
                              alt="Video documental"
                              className="w-full h-32 object-cover"
                           />
                           <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
                              <a
                                 href="https://drive.google.com/file/d/1VT7okdntTURunDvxefBb4kLGGFH2Glqy/view"
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                              >
                                 <Play className="w-5 h-5 text-[#9B6D3F] ml-0.5" />
                              </a>
                           </div>
                        </div>
                        <p className="text-[0.68rem] uppercase tracking-[0.18em] text-[#7A7570]">
                           Ver documental del artista
                        </p>
                     </div>
                  </motion.div>
               </div>
            </div>
         ) : (
            <div className="flex justify-center items-center w-full min-h-[50vh]">
               <p className="text-[0.78rem] uppercase tracking-[0.15em] text-[#7A7570]">
                  No se encontraron obras
               </p>
            </div>
         )}
      </>
   );
}
